import { useState, useRef, useEffect, useCallback } from 'react';
import { NAV_ITEMS } from '@/data/mockData';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { logout, loadSession } from '@/lib/authApi';
import { flushSyncBeforeLogout } from '@/lib/syncEngine';
import { AppleCardAlert } from '@/components/AppleCardAlert';
import { BrandLogo } from '@/components/BrandLogo';
import { getVisibleNavItems, usePermissionRevision } from '@/lib/permissions';
import {  } from 'lucide-react';
import {
  LayoutDashboard, Database, Truck, Package, ShoppingCart,
  Wallet, MapPin, Barcode, TrendingUp, Settings, Sparkles,
  ScanLine, Users, Warehouse, PackagePlus, ListOrdered, History,
  Layers, PackageMinus, AlertTriangle, Receipt, FileText, Undo2,
  TrendingDown, Scale, Map, Route, LayoutTemplate, DollarSign,
  UserCog, Shield, Activity, ChevronRight, ClipboardList,
  Lock, ArchiveRestore, Tag, Cloud, CloudCog, PackageCheck, StickyNote, ImagePlus, Printer,
} from 'lucide-react';

const ICON_MAP: Record<string, React.ComponentType<{ className?: string; strokeWidth?: number }>> = {
  LayoutDashboard, Database, Truck, Package, ShoppingCart,
  Wallet, MapPin, Barcode, TrendingUp, Settings, Sparkles,
  ScanLine, Users, Warehouse, PackagePlus, ListOrdered, History,
  Layers, PackageMinus, AlertTriangle, Receipt, FileText, Undo2,
  TrendingDown, Scale, Map, Route, LayoutTemplate, DollarSign,
  UserCog, Shield, Activity, ClipboardList, Lock, ArchiveRestore, Tag, Cloud, CloudCog, PackageCheck, StickyNote, ImagePlus, Printer,
};

interface SidebarProps {
  currentPage: string;
  onNavigate: (page: string, childId?: string) => void;
  activeChildId?: string;
}

// 二级面板最大高度（标题 + N 个子菜单 + padding 通常 ~470px，多给点富余）
const PANEL_MAX_HEIGHT = 560;

// 系统管理 / 接近底部的一级菜单使用「向上展开」避免被屏幕底裁切
const BOTTOM_ANCHORED_IDS = new Set(['system']);

interface HoveredPanel {
  id: string;
  anchorTop: number;
  anchorBottom: number;
}

export function Sidebar({ currentPage, onNavigate, activeChildId }: SidebarProps) {
  const { user, logoutNow } = useAuth();
  usePermissionRevision();
  const visibleNavItems = getVisibleNavItems(user?.role);
  const [confirmLogout, setConfirmLogout] = useState(false);
  // 二级面板 hover 状态：存一级菜单 ID + 一级菜单 rect（用于 fixed 定位）
  const [hovered, setHovered] = useState<HoveredPanel | null>(null);
  // 进入/离开的 100ms 缓冲，避免鼠标从一级移到二级时瞬间消失
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // nav 滚动/窗口 resize 时刷新 panel 坐标 + 关闭完全滚出视口的 panel
  const navRef = useRef<HTMLElement>(null);

  const handleLogout = async () => {
    setConfirmLogout(false);
    const token = loadSession()?.token;
    // 尽力上传，但退出绝不能因 rejected-stale、网络抖动或超时要求用户重复操作。
    // 被拒绝的旧本地项已由同步引擎撤销快照，下一次登录会以服务器版本为准。
    const syncResult = await flushSyncBeforeLogout();
    if (!syncResult.ok) console.warn('[logout] sync incomplete:', syncResult.error);
    if (token) { try { await logout(token); } catch { /* 本机仍须退出，令牌到期后也不可再用。 */ } }
    logoutNow();
  };

  const displayName = user?.displayName || user?.username || '未登录';
  const avatarChar = displayName.slice(0, 1);

  const isItemActive = (item: typeof NAV_ITEMS[number]) => {
    if (currentPage === item.path) return true;
    if (item.children?.some((c) => c.path === currentPage || activeChildId === c.id)) return true;
    return false;
  };
  const isChildActive = (child: { id: string; path: string }) => {
    return activeChildId === child.id || (activeChildId === undefined && currentPage === child.path);
  };

  const cancelClose = useCallback(() => {
    if (leaveTimer.current) {
      clearTimeout(leaveTimer.current);
      leaveTimer.current = null;
    }
  }, []);

  const enterItem = useCallback((id: string, anchorEl: HTMLDivElement) => {
    cancelClose();
    const r = anchorEl.getBoundingClientRect();
    setHovered({ id, anchorTop: r.top, anchorBottom: r.bottom });
  }, [cancelClose]);

  const scheduleClose = useCallback(() => {
    cancelClose();
    leaveTimer.current = setTimeout(() => setHovered(null), 100);
  }, [cancelClose]);

  // 监听 nav 滚动 + window scroll/resize：刷新 panel 坐标
  useEffect(() => {
    if (!hovered) return;
    const update = () => {
      setHovered((prev) => {
        if (!prev) return null;
        const itemEl = document.getElementById(`sidebar-item-${prev.id}`);
        if (!itemEl) return prev;
        const r = itemEl.getBoundingClientRect();
        // 完全滚出视口则关闭
        if (r.bottom < 0 || r.top > window.innerHeight) return null;
        return { id: prev.id, anchorTop: r.top, anchorBottom: r.bottom };
      });
    };
    const nav = navRef.current;
    nav?.addEventListener('scroll', update, { passive: true });
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      nav?.removeEventListener('scroll', update);
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [hovered]);

  // 卸载清理
  useEffect(() => () => cancelClose(), [cancelClose]);

  return (
    <aside className="hidden xl:flex w-[120px] flex-col bg-[#0f172a] flex-shrink-0 z-50 relative">
      {/* Logo */}
      <div className="h-16 flex items-center justify-center border-b border-white/10 flex-shrink-0">
        <BrandLogo size={44} rounded="rounded-xl" />
      </div>

      {/* Nav：overflow-x-visible 让 fixed 子面板不被裁切 */
      /* 内部 12 项菜单在 27.2 寸窄窗口下可能超出 → nav 自身滚动兜底 */}
      <nav
        ref={navRef}
        className="flex-1 py-3 space-y-1 overflow-y-auto overflow-x-visible scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent hover:scrollbar-thumb-white/20"
      >
        {visibleNavItems.map((item) => {
          const Icon = ICON_MAP[item.icon] || LayoutDashboard;
          const active = isItemActive(item);
          const hasChildren = !!(item.children && item.children.length > 0);

          return (
            <div
              key={item.id}
              id={`sidebar-item-${item.id}`}
              className="group/item relative"
              onMouseEnter={(e) => hasChildren && enterItem(item.id, e.currentTarget)}
              onMouseLeave={hasChildren ? scheduleClose : undefined}
            >
              {/* Active background card (replaces left bar — cleaner look) */}
              {active && (
                <div className="absolute inset-x-1 inset-y-1 rounded-xl bg-emerald-600/15 ring-1 ring-emerald-400/20 z-0" />
              )}

              <button
                onClick={() => {
                  if (!hasChildren) onNavigate(item.path);
                }}
                className={cn(
                  'relative z-10 w-full flex flex-col items-center gap-1.5 py-2.5 px-1 transition-colors duration-150',
                  active
                    ? 'text-white'
                    : 'text-white/85 hover:text-white'
                )}
              >
                <div className={cn(
                  'w-11 h-11 rounded-xl flex items-center justify-center transition-colors duration-150',
                  active ? '' : 'hover:bg-white/5'
                )}>
                  <Icon className={cn('w-[22px] h-[22px]', active && 'text-emerald-400')} strokeWidth={1.75} />
                </div>
                <span
                  className="text-[16px] text-center px-0 font-medium leading-[1.25] tracking-[0.06em]"
                >
                  {item.label}
                </span>
              </button>
            </div>
          );
        })}

        {/* 集中渲染的二级面板：fixed 定位，脱离 nav overflow，自适应高度 */}
        {hovered && (() => {
          const item = visibleNavItems.find((n) => n.id === hovered.id);
          if (!item || !item.children?.length) return null;
          const Icon = ICON_MAP[item.icon] || LayoutDashboard;
          const vh = window.innerHeight;
          // 优先向上展开：固定收尾类（如系统管理）或下方剩余空间 < 380
          const bottomAlign =
            BOTTOM_ANCHORED_IDS.has(item.id) ||
            vh - hovered.anchorBottom < 380;
          const PAD = 8;

          if (bottomAlign) {
            const bottomVh = vh - hovered.anchorBottom + 8;
            return (
              <div
                onMouseEnter={cancelClose}
                onMouseLeave={scheduleClose}
                className="fixed left-[120px] z-[100]"
                style={{ bottom: bottomVh, width: 288 }}
                role="menu"
              >
                <SubPanel Icon={Icon} item={item} isChildActive={isChildActive} onNavigate={onNavigate} bottomAlign />
              </div>
            );
          }
          const maxH = Math.max(180, Math.min(PANEL_MAX_HEIGHT, vh - hovered.anchorTop - PAD));
          return (
            <div
              onMouseEnter={cancelClose}
              onMouseLeave={scheduleClose}
              className="fixed left-[120px] z-[100]"
              style={{ top: hovered.anchorTop, width: 288, maxHeight: maxH }}
              role="menu"
            >
              <SubPanel Icon={Icon} item={item} isChildActive={isChildActive} onNavigate={onNavigate} />
            </div>
          );
        })()}
      </nav>

      {/* User */}
      <div className="p-3 border-t border-white/10 flex-shrink-0 bg-[#0f172a]">
        <button
          type="button"
          onClick={() => setConfirmLogout(true)}
          title={`${displayName} · 点击退出登录`}
          className="w-full flex flex-col items-center gap-1.5 group"
        >
          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-emerald-600 to-emerald-800 flex items-center justify-center text-white text-[14px] font-semibold shadow-lg shadow-emerald-900/30 group-hover:ring-2 group-hover:ring-red-400/60 transition">
            {avatarChar}
          </div>
          <div className="text-[13px] text-slate-200 truncate max-w-full px-1">{displayName}</div>
        </button>
      </div>

      <AppleCardAlert
        open={confirmLogout}
        type="confirm"
        title="退出登录"
        message={`确定要退出「${displayName}」吗？本机数据会保留，下次登录后继续同步。`}
        confirmText="退出"
        cancelText="取消"
        onClose={() => setConfirmLogout(false)}
        onConfirm={handleLogout}
      />
    </aside>
  );
}

/* 抽出二级面板组件，避免在 map 里 JSX 嵌套三目 */
function SubPanel({
  Icon,
  item,
  maxH,
  isChildActive,
  onNavigate,
}: {
  Icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  item: typeof NAV_ITEMS[number];
  maxH?: number;
  bottomAlign?: boolean;
  isChildActive: (child: { id: string; path: string }) => boolean;
  onNavigate: (page: string, childId?: string) => void;
}) {
  const innerMaxH = maxH !== undefined ? Math.max(maxH - 95, 140) : 560;
  return (
    <div className="bg-[#0f172a] rounded-xl border border-white/10 shadow-2xl shadow-black/40 py-3.5 px-3.5">
      <div className="flex items-center gap-3 px-2 pb-3 mb-2.5 border-b border-white/10">
        <div className="w-9 h-9 rounded-lg bg-emerald-600/15 flex items-center justify-center">
          <Icon className="w-5 h-5 text-emerald-400" strokeWidth={1.75} />
        </div>
        <span className="text-[17px] font-medium text-white tracking-[0.04em]">{item.label}</span>
      </div>
      <div
        className="space-y-1 overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-white/10"
        style={{ maxHeight: innerMaxH }}
      >
        {item.children!.map((child) => {
          const ChildIcon = ICON_MAP[child.icon] || LayoutDashboard;
          const active = isChildActive(child);
          return (
            <button
              key={child.id}
              onClick={() => onNavigate(child.path, child.id)}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[15px] leading-[1.3] tracking-[0.02em] transition-colors',
                active
                  ? 'bg-emerald-600/15 text-white font-medium'
                  : 'text-white/85 hover:bg-white/5 hover:text-white font-normal'
              )}
            >
              <ChildIcon
                className={cn('w-5 h-5 flex-shrink-0', active ? 'text-emerald-400' : 'text-white/55')}
                strokeWidth={1.75}
              />
              <span className="flex-1 text-left">{child.label}</span>
              {active && <ChevronRight className="w-4 h-4 text-emerald-400" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}
